import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import BottomNav from './navbar/BottomNav';
import MobileChatModal from './navbar/MobileChatModal';
import DashboardTopNav from './navbar/DashboardTopNav';
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from '@/contexts/auth';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AIAgentChatProvider } from '@/contexts/ai-agents';
import { LocalStorageStateProvider, useLocalStorageState } from '@/contexts/ai-agents/LocalStorageStateContext';
import { AISystemBackground } from '@/components/ai-agents';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { cn } from '@/lib/utils';
import { useQuest } from '@/contexts/QuestContext';
import { QuestCard } from '@/components/onboarding/QuestCard';
import { QuestStep } from '@/types/quest-types';
import { supabase } from '@/integrations/supabase/client';


// Pages that don't require a workspace to access
const PUBLIC_DASHBOARD_PAGES = [
  '/home',
  '/task-manager',
  '/image-generator',
  '/account',
];

// Layout transition animations
const layoutTransition = {
  type: "spring",
  stiffness: 300,
  damping: 30,
  duration: 0.3
};

/**
 * Inner component that uses the SidebarProvider context
 */
const DashboardLayoutInner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const { isModalOpen, closeModal } = useLocalStorageState();
  const { user } = useAuth();
  const { 
    questState, 
    currentQuestStep, 
    startQuest, 
    skipQuest,
    skipCurrentStep,
    completeQuest,
    getCurrentQuestStepWithResolvedUrl,
    canProgressToProjectSteps,
    isOnCorrectPage,
    isLoading
  } = useQuest();

  // State for quest card visibility
  const [shouldShowQuestCard, setShouldShowQuestCard] = useState(false);
  const [resolvedQuestStep, setResolvedQuestStep] = useState<QuestStep | null>(null);

  // Auto-start quest when user reaches home page for the first time
  useEffect(() => {
    // Check if user has never started the quest (onboarding_quest is null/undefined)
    // This happens after they complete /welcome and reach /home
    if (location.pathname === '/home' && questState.currentStep === 1 && !questState.isActive && !questState.isCompleted) {
      // Start the quest automatically
      startQuest();
    }
  }, [location.pathname, questState, startQuest]);

  // Effect to resolve quest step and check if it should be shown
  useEffect(() => {
    const checkQuestVisibility = async () => {
      // Hide quest card while loading
      if (isLoading) {
        setShouldShowQuestCard(false);
        setResolvedQuestStep(null);
        return;
      }
      
      // Hide quest card on mobile
      if (isMobile) {
        setShouldShowQuestCard(false);
        setResolvedQuestStep(null);
        return;
      }
      
      if (!questState.isActive || questState.isCompleted || !currentQuestStep) {
        setShouldShowQuestCard(false);
        setResolvedQuestStep(null);
        return;
      }

      // For project steps (5-8), check if we can progress, but don't hide quest - let it redirect to CREATE_PROJECT
      if (currentQuestStep.id >= 5) {
        const canProgress = await canProgressToProjectSteps();
        if (!canProgress) {
          console.log('[UnifiedDashboardLayout] Cannot progress to project steps, redirecting to CREATE_PROJECT step');
          // Show CREATE_PROJECT step instead of hiding quest
          const createProjectStep = await getCurrentQuestStepWithResolvedUrl();
          if (createProjectStep && createProjectStep.id === 4) {
            setResolvedQuestStep(createProjectStep);
            setShouldShowQuestCard(true);
            return;
          }
        }
      }

      // Resolve the quest step URL
      const resolved = await getCurrentQuestStepWithResolvedUrl();
      if (resolved) {
        console.log('[UnifiedDashboardLayout] Resolved quest step:', resolved);
        setResolvedQuestStep(resolved);
        setShouldShowQuestCard(true);
      } else {
        setShouldShowQuestCard(false);
        setResolvedQuestStep(null);
      }
    };

    checkQuestVisibility();
  }, [questState, currentQuestStep, canProgressToProjectSteps, getCurrentQuestStepWithResolvedUrl, isMobile, isLoading]);

  // Quest navigation handler
  const handleQuestNavigation = useCallback(async () => {
    if (resolvedQuestStep) {
      // Special handling for celebration step (step 9)
      if (resolvedQuestStep.id === 9) {
        console.log('[UnifiedDashboardLayout] Completing quest celebration');
        // Mark quest as fully completed by incrementing progress beyond 8
        completeQuest(9);
        return;
      }
      
      console.log('[UnifiedDashboardLayout] Navigating to:', resolvedQuestStep.page);
      // Use React Router for smooth navigation with a small delay to ensure state is ready
      setTimeout(() => {
        navigate(resolvedQuestStep.page);
      }, 100);
    }
  }, [resolvedQuestStep, navigate, completeQuest]);
  
  // 🤖 AI CONTEXT: Universal cache population for all authenticated users
  // 🤖 AI CONTEXT: Cache now populated by database-driven system in AuthProvider
  
  // Check if current path is in public dashboard pages
  const isPublicDashboardPage = PUBLIC_DASHBOARD_PAGES.some(path => 
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  // Layout classes - content area now properly constrained between navbars
  const contentAreaClasses = cn(
    "flex-1 overflow-y-scroll overflow-x-hidden", // Always show vertical scrollbar, never horizontal
    "transition-all duration-300 ease-in-out", // Smooth transitions
    "relative" // For proper positioning
  );

  // Updated padding - reduced from pb-20 to pb-16 to match navbar height
  const mainClasses = cn(
    "transition-all duration-300 ease-in-out", // Smooth padding transitions
    'pt-2 pb-16', // Updated spacing - pb-16 matches navbar height (64px)
    "px-2",
    "min-h-full" // Ensure full height
  );

  return (
    <AIAgentChatProvider>
      <div className="relative flex h-screen w-full flex-col overflow-hidden">
        {/* AI System Background - provides signature gradient environment */}
        <AISystemBackground />
        
        {/* Top navigation - always present */}
        <DashboardTopNav />
        
        {/* Main body container - now properly constrained between navbars */}
        <div id="DashboardBodyContainer" className="flex flex-1 overflow-hidden">
          <div className="flex h-full w-full">
            {/* Content area - takes full width, clean scrollbar positioning */}
            <div className={contentAreaClasses}>
              {/* Main content - no longer contains bottom nav */}
              <main className={mainClasses}>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, delay: 0.1 }}
                  className="h-full"
                >
                  <Outlet />
                </motion.div>
              </main>
            </div>
          </div>
        </div>

        {/* Bottom navigation - moved to same hierarchy level as top nav */}
        <div className="sticky bottom-0 z-100 w-full border-t bg-background/80 backdrop-blur-md transition-all duration-300">
          <BottomNav />
        </div>

        {/* Chat Modal - universal modal with responsive content */}
        <MobileChatModal 
          isOpen={isModalOpen} 
          onClose={closeModal} 
        />

        {/* Quest Debug Panel - Uncomment to show debug controls
        <div className="fixed top-4 left-4 z-40 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-xs">
          <div className="text-yellow-700 dark:text-yellow-300 mb-2">
            Quest: {questState.isActive ? 'Active' : questState.isCompleted ? 'Completed' : 'Inactive'} | 
            Step: {questState.currentStep}/8 | 
            Done: {questState.completedSteps.length}
          </div>
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={startQuest}
              className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
            >
              Start Quest
            </button>
            {questState.currentStep > 1 && questState.isActive && (
              <button
                onClick={async () => {
                  // Go back to previous step
                  const prevStep = questState.currentStep - 2; // -2 because we want to set completed steps
                  const { error } = await supabase
                    .from('users')
                    .update({ onboarding_quest: prevStep })
                    .eq('id', user?.id);
                  
                  if (!error) {
                    // Force reload quest state
                    window.location.reload();
                  }
                }}
                className="px-2 py-1 bg-orange-500 text-white rounded text-xs hover:bg-orange-600"
              >
                Prev Step
              </button>
            )}
            {currentQuestStep && (
              <button
                onClick={() => completeQuest(currentQuestStep.id)}
                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
              >
                Skip Step
              </button>
            )}
            <button
              onClick={skipQuest}
              className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
            >
              End Quest
            </button>
          </div>
        </div>
        */}

        {/* Quest Card - Shows on all pages when quest is active and valid */}
        {shouldShowQuestCard && resolvedQuestStep && (
          <QuestCard
            questStep={resolvedQuestStep}
            currentStep={questState.currentStep}
            totalSteps={8}
            completedSteps={questState.completedSteps}
            onComplete={handleQuestNavigation}
            onSkip={skipQuest}
            isOnCorrectPage={isOnCorrectPage(resolvedQuestStep)}
          />
        )}
      </div>
    </AIAgentChatProvider>
  );
};

/**
 * UnifiedDashboardLayout - Now uses mobile layout for all screen sizes
 */
const UnifiedDashboardLayout = () => {
  const { isAuthenticated } = useAuth();

  return (
    <TooltipProvider>
      <Toaster />
      <LocalStorageStateProvider>
        <SidebarProvider defaultOpen={false}>
          <DashboardLayoutInner />
        </SidebarProvider>
      </LocalStorageStateProvider>
    </TooltipProvider>
  );
};

export default UnifiedDashboardLayout;
